import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Show, UserButton, useAuth } from "@clerk/react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { useAdminApi } from "@/lib/admin";
import { MemberOverview } from "@/components/member/MemberOverview";
import { MemberTeam } from "@/components/member/MemberTeam";
import { MemberEarnings } from "@/components/member/MemberEarnings";
import { MemberOrders } from "@/components/member/MemberOrders";
import { MemberInbox } from "@/components/member/MemberInbox";
import { MemberBuy } from "@/components/member/MemberBuy";

const TABS = [["overview", "Overview"], ["buy", "Buy Products / Order Packages"], ["team", "Team Network"], ["earnings", "Sales, Commission & Wallet"], ["orders", "Payments & Shop Orders"], ["inbox", "Notifications & Support"]];

export const useMember = (url) => {
  const call = useAdminApi();
  const [data, setData] = useState(null);
  const reload = useCallback(() => call("get", url).then(setData).catch((e) => toast.error(errMsg(e))), [call, url]);
  useEffect(() => {
    reload();
  }, [reload]);
  return [data, reload, call];
};

const StatusCard = ({ reg }) => {
  const msg = {
    pending: "Your registration is under verification by the BMI SP GROUP team. You'll receive your Distributor ID once approved.",
    rejected: "Your registration was not approved. You can correct your details and register again.",
    suspended: "Your distributor account is suspended. Please contact customer support.",
  }[reg.status];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="member-status-card">
      <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Registration status</p>
      <p className="mt-1 text-2xl font-bold capitalize" data-testid="member-status">{reg.status}</p>
      <p className="mt-2 text-slate-600">{msg}</p>
      {reg.status === "rejected" && <Link to="/distributor/register" className="mt-4 inline-block rounded-full bg-amber-600 px-5 py-2 text-sm font-semibold text-white" data-testid="member-reregister">Register again</Link>}
    </div>
  );
};

const Dashboard = () => {
  const [me] = useMember("/mlm/me");
  const [tab, setTab] = useState("overview");
  if (!me) return <p className="text-slate-500">Loading…</p>;
  const reg = me.registration;
  if (!reg)
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="member-not-registered">
        <p className="text-lg font-semibold">You are signed in as {me.account_email} but have not registered as a distributor yet.</p>
        <Link to="/distributor/register" className="mt-4 inline-block rounded-full bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white" data-testid="member-register-cta">Complete distributor registration</Link>
      </div>
    );
  if (reg.status !== "approved") return <StatusCard reg={reg} />;
  return (
    <div>
      <div className="mb-6 flex gap-2 overflow-x-auto pb-1">
        {TABS.map(([id, l]) => (
          <button key={id} onClick={() => setTab(id)} className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === id ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`} data-testid={`member-tab-${id}`}>{l}</button>
        ))}
      </div>
      {tab === "overview" && <MemberOverview />}
      {tab === "buy" && <MemberBuy />}
      {tab === "team" && <MemberTeam />}
      {tab === "earnings" && <MemberEarnings />}
      {tab === "orders" && <MemberOrders />}
      {tab === "inbox" && <MemberInbox />}
    </div>
  );
};

export default function Member() {
  const { isLoaded } = useAuth();
  if (!isLoaded) return <p className="container-x py-16 text-slate-500">Loading…</p>;
  return (
    <div className="container-x py-12">
      <div className="mb-8 flex items-center justify-between gap-4">
        <div><p className="text-xs font-bold uppercase tracking-widest text-amber-600">Member dashboard</p><h1 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl">My distributor account</h1></div>
        <UserButton />
      </div>
      <Show when="signed-in"><Dashboard /></Show>
      <Show when="signed-out"><Navigate to="/member/login" replace /></Show>
    </div>
  );
}
